import React, { useState } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { useQueryClient, useMutation, useQuery } from 'react-query';
import { supabase } from '../../lib/supabaseClient';

interface PaymentGateway {
  id: string;
  name: string;
}

interface PaymentMethod {
  id: string;
  gateway_id: string;
  name: string;
  description: string;
  icon: string;
  is_active: boolean;
  metadata: {
    useDirectLink?: boolean;
    useDirectPayment?: boolean;
    subscriptionUrls?: Record<string, string>;
    planIds?: Record<string, string>;
  };
}

interface PaymentMethodFormProps {
  gateway: PaymentGateway;
  method?: PaymentMethod | null;
  onClose: () => void;
}

const ICON_OPTIONS = [
  { value: 'credit-card', label: 'Credit Card' },
  { value: 'bitcoin', label: 'Bitcoin/Crypto' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'landmark', label: 'Bank/Building' },
  { value: 'smartphone', label: 'Mobile Phone' },
  { value: 'paypal', label: 'PayPal' }
];

export const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({ 
  gateway, 
  method, 
  onClose 
}) => {
  const [name, setName] = useState(method?.name || '');
  const [description, setDescription] = useState(method?.description || '');
  const [icon, setIcon] = useState(method?.icon || 'credit-card');
  const [isActive, setIsActive] = useState(method?.is_active ?? true);
  const [useDirectLink, setUseDirectLink] = useState(method?.metadata?.useDirectLink ?? false);
  const [useDirectPayment, setUseDirectPayment] = useState(method?.metadata?.useDirectPayment ?? false);
  const [subscriptionUrls, setSubscriptionUrls] = useState<Record<string, string>>(
    method?.metadata?.subscriptionUrls || {}
  );
  const [planIds, setPlanIds] = useState<Record<string, string>>(
    method?.metadata?.planIds || {}
  );
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  // Fetch subscription plans
  const { data: plans = [], isLoading: plansLoading } = useQuery(
    'subscription-plans',
    async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('id, name')
        .eq('is_active', true);

      if (error) throw error;
      return data;
    }
  );

  const { mutate: saveMethod, isLoading } = useMutation(
    async () => {
      const data = {
        gateway_id: gateway.id,
        name,
        description,
        icon,
        is_active: isActive,
        metadata: {
          useDirectLink,
          useDirectPayment,
          subscriptionUrls: useDirectLink ? subscriptionUrls : {},
          planIds: !useDirectLink ? planIds : {}
        }
      };

      if (method?.id) {
        const { error } = await supabase
          .from('payment_methods')
          .update(data)
          .eq('id', method.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('payment_methods')
          .insert([data]);
        if (error) throw error;
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('payment-methods');
        onClose();
      },
      onError: (error: Error) => {
        setError(error.message);
      }
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate inputs
    if (!name) {
      setError('Method name is required');
      return;
    }

    if (!icon) {
      setError('Icon is required');
      return;
    }

    saveMethod();
  };

  const handleSubscriptionUrlChange = (planId: string, url: string) => {
    setSubscriptionUrls(prev => ({
      ...prev,
      [planId]: url
    }));
  };

  const handlePlanIdChange = (planId: string, externalPlanId: string) => {
    setPlanIds(prev => ({
      ...prev,
      [planId]: externalPlanId
    }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
          onClick={onClose}
        />
        
        <div className="inline-block w-full max-w-md my-8 text-left align-middle transition-all transform">
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="absolute right-4 top-4 p-2 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {method ? 'Edit Payment Method' : 'Add Payment Method'}
              </h2>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-6">
                {gateway.name} Gateway
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Method Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Credit Card"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Pay with Visa, Mastercard, or American Express"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Icon
                  </label>
                  <select
                    value={icon}
                    onChange={(e) => setIcon(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                    disabled={isLoading}
                  >
                    {ICON_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={isLoading}
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900 dark:text-white">
                    Active
                  </label>
                </div>

                <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                    Payment Processing Method
                  </h3>
                  
                  <div className="space-y-3 mb-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="paymentType"
                        checked={useDirectLink && !useDirectPayment}
                        onChange={() => {
                          setUseDirectLink(true);
                          setUseDirectPayment(false);
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        disabled={isLoading}
                      />
                      <span className="text-sm text-gray-900 dark:text-white">
                        Direct Link (Redirect to external URL)
                      </span>
                    </label>
                    
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="paymentType"
                        checked={!useDirectLink && !useDirectPayment}
                        onChange={() => {
                          setUseDirectLink(false);
                          setUseDirectPayment(false);
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        disabled={isLoading}
                      />
                      <span className="text-sm text-gray-900 dark:text-white">
                        Plan ID (Process via backend)
                      </span>
                    </label>
                    
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="paymentType"
                        checked={useDirectPayment}
                        onChange={() => {
                          setUseDirectLink(false);
                          setUseDirectPayment(true);
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        disabled={isLoading}
                      />
                      <span className="text-sm text-gray-900 dark:text-white">
                        Direct Payment (One-time checkout)
                      </span>
                    </label>
                  </div>
                </div>

                {/* Subscription Plan Configuration */}
                {!plansLoading && plans.length > 0 && !useDirectPayment && (
                  <div className="mt-6">
                    <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                      {useDirectLink ? 'Subscription Plan URLs' : 'External Plan IDs'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      {useDirectLink 
                        ? 'Enter the subscription URLs for each plan. These will be used to redirect users to the payment platform.'
                        : 'Enter the external plan IDs for each plan. These will be used to process payments via the backend.'}
                    </p>
                    
                    <div className="space-y-4">
                      {plans.map(plan => (
                        <div key={plan.id}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {plan.name}
                          </label>
                          {useDirectLink ? (
                            <input
                              type="url"
                              value={subscriptionUrls[plan.id] || ''}
                              onChange={(e) => handleSubscriptionUrlChange(plan.id, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder={`https://example.com/subscribe/${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                              disabled={isLoading}
                            />
                          ) : (
                            <input
                              type="text"
                              value={planIds[plan.id] || ''}
                              onChange={(e) => handlePlanIdChange(plan.id, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="External plan ID"
                              disabled={isLoading}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {useDirectPayment && (
                  <div className="mt-6">
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Direct Payment will process payments through the backend server without requiring subscription URLs or plan IDs.
                        Users will be redirected to a checkout page where they can complete their payment.
                      </p>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Method'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};